// Google Authentication Service
export interface GoogleUser {
  id: string;
  name: string;
  email: string;
  picture: string;
  given_name: string;
  family_name: string;
}

export interface GoogleAuthResponse {
  credential: string;
  select_by: string;
}

declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleAuthResponse) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: string | number;
              locale?: string;
            }
          ) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

class GoogleAuthService {
  private clientId: string;
  private isInitialized: boolean = false;

  constructor() {
    // Read Google Client ID from environment variable
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    console.log('Google Client ID configured:', !!this.clientId);
    if (!this.clientId) {
      console.error('REACT_APP_GOOGLE_CLIENT_ID not found in environment variables');
    }
  }

  // Initialize Google Identity Services
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    return new Promise((resolve, reject) => {
      // Check if Google Identity Services is already loaded
      if (window.google?.accounts?.id) {
        this.initializeGoogleAuth();
        this.isInitialized = true;
        resolve();
        return;
      }
      // Dynamically load Google Identity Services script
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        this.initializeGoogleAuth();
        this.isInitialized = true;
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services'));
      };

      document.head.appendChild(script);
    });
  }

  private initializeGoogleAuth(): void {
    if (!this.clientId) {
      console.error('Google Client ID not configured');
      return;
    }

    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: this.handleCredentialResponse.bind(this),
      auto_select: false,
      cancel_on_tap_outside: true,
    });
  }

  // Handle Google authentication response
  private handleCredentialResponse(response: GoogleAuthResponse): void {
    try {
      // Decode JWT token to get user info
      const userInfo = this.parseJwt(response.credential);
      console.log('Google login successful:', userInfo);
      
      // Trigger custom event to notify app of successful login
      const loginEvent = new CustomEvent('googleLoginSuccess', {
        detail: userInfo
      });
      window.dispatchEvent(loginEvent);
      
    } catch (error) {
      console.error('Google login failed:', error);
      
      // Trigger login error event
      const errorEvent = new CustomEvent('googleLoginError', {
        detail: error
      });
      window.dispatchEvent(errorEvent);
    }
  }

  // Parse JWT token
  private parseJwt(token: string): GoogleUser {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window.atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new Error('Invalid JWT token');
    }
  }

  // Render Google login button
  renderButton(element: HTMLElement): void {
    if (!this.isInitialized) {
      console.error('Google Auth Service not initialized');
      return;
    }

    window.google.accounts.id.renderButton(element, {
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 400 // 使用数字而不是百分比字符串
    });
  }

  // Show one-tap login prompt
  showOneTap(): void {
    if (!this.isInitialized) {
      console.error('Google Auth Service not initialized');
      return;
    }

    window.google.accounts.id.prompt();
  }

  // Disable auto select
  disableAutoSelect(): void {
    if (!this.isInitialized) return;
    window.google.accounts.id.disableAutoSelect();
  }

  // Get client ID (for debugging)
  getClientId(): string {
    return this.clientId;
  }

  // Check if configured
  isConfigured(): boolean {
    return !!this.clientId;
  }
}

// Export singleton instance
export const googleAuthService = new GoogleAuthService();
